import { Options } from '@splunk/visualization-encoding/Options';

type OptionsEvaluateParams = Parameters<typeof Options.evaluate>;
type OptionsEvaluateResult = ReturnType<typeof Options.evaluate>;

type ThemeFunction = (props?: unknown) => unknown;
type ThemeFunctionMap = Record<string, ThemeFunction>;
type EvaluateMessageType = 'evaluate';

export interface EvaluateMessageData {
    message: EvaluateMessageType;
    visualizationID: string;
    options: OptionsEvaluateParams[0];
    dataSources: OptionsEvaluateParams[1];
    themeFuncResolved: Record<string, unknown>;
    bypassCloneDeepOptionScope?: OptionsEvaluateParams[4];
    lazyLoadingDataFrame?: OptionsEvaluateParams[5];
    enableTagSearchResult?: OptionsEvaluateParams[6];
    props?: unknown;
}

interface HandleMessageEvent {
    data?: Partial<EvaluateMessageData> & { message?: EvaluateMessageType };
}

interface HandleMessageSuccess {
    visualizationID: string | undefined;
    results: OptionsEvaluateResult;
}

interface HandleMessageFailure {
    visualizationID: string | undefined;
    error: true;
}

type HandleMessageReturn = HandleMessageSuccess | HandleMessageFailure | Record<string, never>;

const buildThemeFunction = (resolvedTheme: Record<string, unknown> = {}): ThemeFunctionMap => {
    return Object.keys(resolvedTheme).reduce<ThemeFunctionMap>((acc, key) => {
        const resolver: ThemeFunction = () => resolvedTheme[key];
        acc[key] = resolver;
        return acc;
    }, {} as ThemeFunctionMap);
};

const handleMessage = (event: HandleMessageEvent): HandleMessageReturn => {
    if (event?.data?.message !== 'evaluate') {
        return {};
    }

    const {
        props,
        options,
        dataSources,
        themeFuncResolved,
        bypassCloneDeepOptionScope,
        lazyLoadingDataFrame,
        enableTagSearchResult,
        visualizationID,
    } = event.data;

    if (!options || !dataSources || !themeFuncResolved) {
        return { visualizationID, error: true };
    }

    try {
        const themeFuncUnresolved = buildThemeFunction(themeFuncResolved);
        const themeFunc = (themeVar: string) => themeFuncUnresolved[themeVar]?.(props);

        const results = Options.evaluate(
            options,
            dataSources,
            themeFunc,
            null,
            bypassCloneDeepOptionScope,
            lazyLoadingDataFrame,
            enableTagSearchResult
        );

        return { visualizationID, results };
    } catch (error) {
        return { visualizationID, error: true };
    }
};

export default handleMessage;
